import { getInboxUpdateStatus, updateInboxFunnels } from 'API/linkedin-funnel';
import { useCallback, useEffect, useRef, useState } from 'react';

export interface InboxUpdateProgress {
  /** Accounts that have been successfully updated since the request. */
  updatedCount: number;
  /** Total accounts queued for update. */
  totalCount: number;
}

interface UseInboxUpdateOptions {
  /** Called when all accounts have been updated. */
  onComplete?(): void;
  /** Called if the update is aborted due to a timeout. */
  onTimeout?(): void;
  /** Max time to poll before giving up, in milliseconds. Default: 3 minutes. */
  timeoutMs?: number;
  /** Interval between status polls, in milliseconds. Default: 4 seconds. */
  pollIntervalMs?: number;
}

/**
 * Manages an ad-hoc inbox update lifecycle:
 *  1. POST /messages/inbox/update  → fires BullMQ jobs, gets back accountIds + requestedAt
 *  2. Polls GET /messages/inbox/update/status every `pollIntervalMs`
 *  3. Resolves when all accounts report lastInboxUpdate > requestedAt, or after `timeoutMs`
 *
 * The hook is deliberately fire-and-observe: the HTTP requests are fast (no long poll),
 * and the UI stays responsive with a live progress indicator.
 */
export const useInboxUpdate = (options: UseInboxUpdateOptions = {}) => {
  const {
    onComplete,
    onTimeout,
    timeoutMs = 3 * 60 * 1000,
    pollIntervalMs = 4000,
  } = options;

  const [isUpdating, setIsUpdating] = useState(false);
  const [progress, setProgress] = useState<InboxUpdateProgress | null>(null);

  const isUpdatingRef = useRef(false);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      stopPolling();
    };
  }, []);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current !== null) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  const triggerUpdate = useCallback(
    async (
      type: string | undefined,
      searchParam: string,
      funnelFilter: string[],
      liAccountFilter: string[],
      since?: string,
    ) => {
      if (isUpdatingRef.current) return;

      isUpdatingRef.current = true;
      setIsUpdating(true);
      setProgress(null);
      stopPolling();

      abortControllerRef.current = new AbortController();

      let requestedAt: string;
      let accountIds: string[];

      try {
        const result = await updateInboxFunnels(
          type,
          searchParam,
          funnelFilter,
          liAccountFilter,
          abortControllerRef.current.signal,
          since,
        );
        requestedAt = result.requestedAt;
        accountIds = result.accountIds;

        if (!isMountedRef.current) return;

        if (accountIds.length === 0) {
          isUpdatingRef.current = false;
          setIsUpdating(false);
          onComplete?.();
          return;
        }

        setProgress({ updatedCount: 0, totalCount: accountIds.length });
      } catch {
        if (isMountedRef.current) {
          isUpdatingRef.current = false;
          setIsUpdating(false);
        }
        return;
      }

      // Start polling status
      const pollStatus = async () => {
        if (!isMountedRef.current) return;

        try {
          const status = await getInboxUpdateStatus(
            accountIds,
            requestedAt,
            abortControllerRef.current?.signal,
          );

          if (!isMountedRef.current) return;

          setProgress({
            updatedCount: status.updatedCount,
            totalCount: status.totalCount,
          });

          if (status.done) {
            stopPolling();
            isUpdatingRef.current = false;
            setIsUpdating(false);
            onComplete?.();
          }
        } catch {
          // Network glitch — keep polling
        }
      };

      // Kick off first poll immediately, then set up repeating interval if not already done
      await pollStatus();

      if (isMountedRef.current && isUpdatingRef.current) {
        pollIntervalRef.current = setInterval(pollStatus, pollIntervalMs);
      }

      // Hard timeout — give up after timeoutMs even if accounts haven't all reported back.
      timeoutRef.current = setTimeout(() => {
        if (!isMountedRef.current) return;
        stopPolling();
        isUpdatingRef.current = false;
        setIsUpdating(false);
        onTimeout?.();
      }, timeoutMs);
    },
    [onComplete, onTimeout, pollIntervalMs, stopPolling, timeoutMs],
  );

  return { isUpdating, progress, triggerUpdate };
};
